import * as Sentry from '@sentry/vue'

/**
 * Шаги пути клиента. Строками их не пишем: по этим именам ищут в Sentry,
 * и опечатка оборачивается молча потерянным куском воронки.
 */
export const FLOW = {
  appOpened: 'app_opened',
  /** Открыли экран выбора точки: карта у доставки, список филиалов у самовывоза */
  mapOpened: 'map_opened',
  /** Кнопка «я здесь» */
  mapLocateUsed: 'map_locate_used',
  /** Адрес выбран подсказкой, а не пином */
  mapSuggestPicked: 'map_suggest_picked',
  /** Взяли готовый адрес из списка сохранённых */
  addressPicked: 'address_picked',
  /** Точку приняло меню — она в зоне доставки */
  pointConfirmed: 'point_confirmed',
  /** Меню отказало: вне зоны или филиал закрыт */
  pointRejected: 'point_rejected',
  addressSaved: 'address_saved',
  /** Положили товар в корзину */
  cartAdded: 'cart_added',
  checkoutOpened: 'checkout_opened',
  orderSubmitted: 'order_submitted',
  orderFailed: 'order_failed',
  /** Запрос в Яндекс: он платный, и по этим строкам видно расход ключа */
  yandexGeocode: 'yandex_geocode',
  yandexSuggest: 'yandex_suggest',
  /** Запрос к нашему бэкенду не удался — здесь видно, что именно отваливается */
  apiFailed: 'api_failed',
} as const

export type FlowEvent = (typeof FLOW)[keyof typeof FLOW]

/** Числа и флаги, по которым события фильтруют: имён, телефонов и адресов здесь нет. */
type FlowData = Record<string, string | number | boolean>

/**
 * Шаг пути клиента. Уходит двумя дорогами сразу: логом — его видно
 * отдельной лентой, и по нему считается воронка; хлебной крошкой — она
 * прицепляется к ошибке и к записи сессии, так что по падению сразу видно,
 * что клиент успел сделать до него.
 *
 * Без инициализации (вся разработка) обе вызова — пустышки, так что звать
 * `track` можно откуда угодно, не оглядываясь на окружение.
 */
export function track(event: FlowEvent, data: FlowData = {}): void {
  Sentry.addBreadcrumb({ category: 'flow', message: event, level: 'info', data })
  Sentry.logger.info(event, data)
}

/** Телеграм знает клиента раньше нашего бэкенда — в мини-аппе id есть у всех. */
function telegramId(): string {
  const id = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
  return id ? String(id) : ''
}

/**
 * Кто совершает действия. Проставляется один раз на сессию и дальше сам
 * цепляется к каждому событию, ошибке и записи — Sentry берёт его из своего
 * контекста, перечислять в каждом вызове `track` ничего не нужно.
 *
 * Пока клиент не заведён у бэкенда, своего id у него нет — тогда ведём по
 * телеграму: иначе весь путь до регистрации (а это и есть самая интересная
 * часть воронки) висел бы ничей. Телеграм остаётся отдельной меткой и после
 * регистрации, так что обе половины пути сходятся на одном человеке.
 *
 * Шлём только идентификаторы: имя, телефон и адрес остаются у нас.
 */
export function identify(id: string): void {
  const telegram = telegramId()
  const known = id || (telegram ? `tg:${telegram}` : '')

  Sentry.setUser(known ? { id: known } : null)

  /*
   * Метка идёт двумя способами намеренно: `setTag` видят ошибки, а строки
   * воронки — только атрибуты контекста. Без второго вызова в логах телеграма
   * не будет, и путь до регистрации не с чем будет связать.
   */
  if (telegram) {
    Sentry.setTag('telegram.id', telegram)
    Sentry.setAttribute('telegram.id', telegram)
  }
}
